(() => {
  const mobile = window.matchMedia('(max-width: 749px)');
  const railSelector = [
    '.skovn-product-main__media',
    '.skovn-products__grid',
    '.skovn-recent__rail'
  ].join(',');

  const directItems = (rail) => {
    return Array.from(rail.children)
      .filter((item) => item.matches(
        '.skovn-product-main__media-item, .skovn-product, .skovn-recent__card'
      ))
      .sort((first, second) => first.offsetLeft - second.offsetLeft);
  };

  const railAnimations = new WeakMap();
  const itemScrollLeft = (rail, item) => {
    return item.getBoundingClientRect().left - rail.getBoundingClientRect().left + rail.scrollLeft;
  };

  const closestItemIndex = (rail, items, scrollLeft = rail.scrollLeft) => {
    return items.reduce((closestIndex, item, index) => {
      return Math.abs(itemScrollLeft(rail, item) - scrollLeft) <
        Math.abs(itemScrollLeft(rail, items[closestIndex]) - scrollLeft)
        ? index
        : closestIndex;
    }, 0);
  };

  const animateRail = (rail, targetLeft) => {
    const previousFrame = railAnimations.get(rail);
    if (previousFrame) cancelAnimationFrame(previousFrame);
    const startLeft = rail.scrollLeft;
    const distance = targetLeft - startLeft;
    const duration = rail.matches('.skovn-product-main__media') ? 300 : 220;
    const startedAt = performance.now();
    rail.classList.add('is-skovn-touching');

    const step = (now) => {
      const progress = Math.min(1, (now - startedAt) / duration);
      const eased = 1 - Math.pow(1 - progress, 3);
      rail.scrollLeft = startLeft + distance * eased;
      if (progress < 1) {
        railAnimations.set(rail, requestAnimationFrame(step));
      } else {
        rail.scrollLeft = targetLeft;
        railAnimations.delete(rail);
        requestAnimationFrame(() => rail.classList.remove('is-skovn-touching'));
      }
    };

    railAnimations.set(rail, requestAnimationFrame(step));
  };

  const bindRail = (rail) => {
    if (rail.dataset.skovnTouchRail === 'true') return;
    rail.dataset.skovnTouchRail = 'true';

    let gesture = null;
    let suppressClickUntil = 0;

    const finish = () => {
      if (!gesture) return;
      const horizontal = gesture.axis === 'x';
      const deltaX = gesture.deltaX;
      const elapsed = Math.max(1, performance.now() - gesture.startedAt);
      const startIndex = gesture.startIndex;
      gesture = null;

      if (!horizontal) {
        rail.classList.remove('is-skovn-touching');
        return;
      }
      const items = directItems(rail);
      if (!items.length) {
        rail.classList.remove('is-skovn-touching');
        return;
      }
      const intentionalSwipe = Math.abs(deltaX) >= 24 || Math.abs(deltaX / elapsed) >= .18;
      const targetIndex = intentionalSwipe
        ? Math.min(items.length - 1, Math.max(0, startIndex + (deltaX < 0 ? 1 : -1)))
        : closestItemIndex(rail, items);
      animateRail(rail, itemScrollLeft(rail, items[targetIndex]));
      suppressClickUntil = Date.now() + 350;
    };

    rail.addEventListener('touchstart', (event) => {
      if (!mobile.matches || event.touches.length !== 1) return;
      const runningFrame = railAnimations.get(rail);
      if (runningFrame) {
        cancelAnimationFrame(runningFrame);
        railAnimations.delete(rail);
        rail.classList.remove('is-skovn-touching');
      }
      const touch = event.touches[0];
      const items = directItems(rail);
      gesture = {
        axis: null,
        startX: touch.clientX,
        startY: touch.clientY,
        startScroll: rail.scrollLeft,
        startIndex: items.length ? closestItemIndex(rail, items) : 0,
        startedAt: performance.now(),
        deltaX: 0
      };
    }, { passive: true });

    rail.addEventListener('touchmove', (event) => {
      if (!gesture || event.touches.length !== 1) return;
      const touch = event.touches[0];
      const deltaX = touch.clientX - gesture.startX;
      const deltaY = touch.clientY - gesture.startY;
      const absoluteX = Math.abs(deltaX);
      const absoluteY = Math.abs(deltaY);
      gesture.deltaX = deltaX;

      if (!gesture.axis) {
        if (Math.max(absoluteX, absoluteY) < 8) return;
        if (absoluteX > absoluteY * 1.2) {
          gesture.axis = 'x';
          rail.classList.add('is-skovn-touching');
        } else if (absoluteY > absoluteX * 1.2) {
          gesture.axis = 'y';
        } else {
          return;
        }
      }

      if (gesture.axis !== 'x') return;
      event.preventDefault();
      const maximum = Math.max(0, rail.scrollWidth - rail.clientWidth);
      rail.scrollLeft = Math.min(maximum, Math.max(0, gesture.startScroll - deltaX));
    }, { passive: false });

    rail.addEventListener('touchend', finish, { passive: true });
    rail.addEventListener('touchcancel', finish, { passive: true });
    rail.addEventListener('click', (event) => {
      if (Date.now() > suppressClickUntil) return;
      event.preventDefault();
      event.stopPropagation();
    }, true);
  };

  const bindCardGallery = (gallery) => {
    if (gallery.dataset.skovnTouchGallery === 'true') return;
    gallery.dataset.skovnTouchGallery = 'true';
    let gesture = null;

    gallery.addEventListener('touchstart', (event) => {
      if (!mobile.matches || event.touches.length !== 1) return;
      const touch = event.touches[0];
      gesture = {
        axis: null,
        startX: touch.clientX,
        startY: touch.clientY,
        deltaX: 0
      };
    }, { passive: true });

    gallery.addEventListener('touchmove', (event) => {
      if (!gesture || event.touches.length !== 1) return;
      const touch = event.touches[0];
      const deltaX = touch.clientX - gesture.startX;
      const deltaY = touch.clientY - gesture.startY;
      const absoluteX = Math.abs(deltaX);
      const absoluteY = Math.abs(deltaY);
      gesture.deltaX = deltaX;

      if (!gesture.axis) {
        if (Math.max(absoluteX, absoluteY) < 8) return;
        if (absoluteX > absoluteY * 1.2) {
          gesture.axis = 'x';
        } else if (absoluteY > absoluteX * 1.2) {
          gesture.axis = 'y';
        } else {
          return;
        }
      }

      if (gesture.axis !== 'x') return;
      event.preventDefault();
      event.stopPropagation();
    }, { passive: false });

    const finish = (event) => {
      if (!gesture) return;
      const horizontal = gesture.axis === 'x';
      const deltaX = gesture.deltaX;
      gesture = null;
      if (!horizontal || Math.abs(deltaX) < 22) return;
      event.preventDefault();
      event.stopPropagation();
      const direction = deltaX < 0 ? 'next' : 'previous';
      gallery.querySelector(`[data-skovn-card-direction="${direction}"]`)?.click();
    };

    gallery.addEventListener('touchend', finish, { passive: false });
    gallery.addEventListener('touchcancel', () => {
      gesture = null;
    }, { passive: true });
  };

  const prepare = (root = document) => {
    root.querySelectorAll(railSelector).forEach(bindRail);
    root.querySelectorAll('.skovn-card__media-shell').forEach(bindCardGallery);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => prepare(), { once: true });
  } else {
    prepare();
  }

  document.addEventListener('shopify:section:load', (event) => prepare(event.target));
})();
